import React, { useCallback, useMemo, useState } from 'react';
import { some } from 'lodash';
import { isEmpty } from 'lodash';
import { TooltipV2 as Tooltip, CollapsibleList, TOOLTIP_POSITION } from 'taboola-ultimate-ui';
import { withIndication } from 'modules/errors/components/withIndication';
import { FEATURE_FLAGS, useConfigMatch } from 'modules/taboola-common-frontend-modules/account-configurations';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { createMenuListWithHeader } from '../../../../../../../components/Dropdown/DropdownMenuListWithHeader/DropdownMenuListWithHeader';
import { useFiltersGlobalState } from '../../../../../../../components/Dropdown/DropdownMenuListWithHeader/hooks/filtersGlobalState';
import { useDropdownFiltersMap } from '../../../../../../../components/Dropdown/DropdownMenuListWithHeader/hooks/useDropdownFiltersMap';
import { FormNamespaceProvider } from '../../../../../../taboola-common-frontend-modules/formData/FormNamespaceProvider';
import { TARGETING_TYPES } from '../../../config';
import { MY_AUDIENCES_TYPES } from '../../../config/MyAudiencesTypes';
import TargetingDropdownBox from '../../TargetingDropdownBox/TargetingDropdownBox';
import { TargetingTypeDropdownOption } from '../../TargetingTypeDropdown';
import MyAudienceDropDownOption from '../MyAudienceDropDownOption/MyAudienceDropDownOption';
import { MyAudienceDropDownOptionCheckbox } from '../MyAudienceDropDownOption/MyAudienceDropDownOptionCheckbox';
import { MyAudiencesListItem } from '../MyAudiencesListItem/MyAudiencesListItem';
import { MyAudiencesListItemContentWithValidations } from '../MyAudiencesListItem/MyAudiencesListItemContentWithValidations';
import { useBlockPBPAudience } from './hooks/useBlockPBPAudience';
import { useMyAudiences } from './hooks/useMyAudiences';
import { useMyAudiencesFilters } from './hooks/useMyAudiencesFilters';
import { useMyAudiencesValidation } from './hooks/useMyAudiencesValidation';
import { usePreventPBPAudiencesMixing } from './hooks/usePreventPBPAudienceMixing';
import styles from '../../BrowserTargeting/browserTargetingSmartList.module.scss';
import commonStyles from '../../commonEditor.module.scss';

const tailStyle = { zIndex: 1 };
const disabledTooltip = (
    <Tooltip position={TOOLTIP_POSITION.BOTTOM} arrow duration={0} delay={800} tailStyle={tailStyle}>
        <FormattedMessage
            id="campaign.editor.targeting.my.audiences.exclude.lookalike.disabled.tooltip"
            defaultMessage="Currently, it is not possible to exclude predictive audiences."
        />
    </Tooltip>
);

const nonPbpDisabledTooltip = (
    <Tooltip position={TOOLTIP_POSITION.BOTTOM} arrow duration={0} tailStyle={tailStyle}>
        <FormattedMessage
            id="campaign.editor.targeting.my.audiences.preventPBPMixing.include.disabled.non.pbp.tooltip"
            defaultMessage="This audience type can't be added when a Predictive Audience is selected."
        />
    </Tooltip>
);

const CollapsibleListWithIndication = withIndication(CollapsibleList);
const IncludeItemComponent = ({ index, ...rest }) => {
    return (
        <FormNamespaceProvider field={index} itemKey={rest.item.id}>
            <MyAudiencesListItemContentWithValidations index={index} {...rest} />
        </FormNamespaceProvider>
    );
};

const IncludeCollapsibleList = ({
    includeAudiences,
    targetingType,
    removeMyAudience,
    clearIncludedItems,
    allSegmentsByIdMap,
    isOptionsLoadedSuccessfully,
    rejectedAudiencesList,
    isRejectedAudiencesLoadedSuccessfully,
    isDirty,
}) => {
    const { includeFailedValidationData } = useMyAudiencesValidation(
        allSegmentsByIdMap,
        includeAudiences,
        isOptionsLoadedSuccessfully,
        isDirty
    );

    const getItemHeight = useCallback(item => {
        return item.similarityLevelToSize ? 100 : 55;
    }, []);

    return (
        <FormNamespaceProvider field="campaignTargeting.myAudienceTargeting.include">
            <CollapsibleListWithIndication
                id="campaignTargeting.myAudienceTargeting.include"
                containerClassName={styles['list']}
                items={includeAudiences}
                itemHeight={getItemHeight}
                ItemComponent={IncludeItemComponent}
                metadata={{
                    targetingType,
                    isReady: isOptionsLoadedSuccessfully && isRejectedAudiencesLoadedSuccessfully,
                    allSegmentsByIdMap,
                    rejectedAudiencesList,
                    isDirty,
                }}
                deleteItem={removeMyAudience}
                clearItems={clearIncludedItems}
                listHeaderTitle={
                    <FormattedMessage
                        id="campaign.editor.targeting.my.audiences.title.included"
                        defaultMessage="Included Audiences"
                    />
                }
                aria-label="include-list"
                showLessLabel={<FormattedMessage id="app.actionButtons.show.less" defaultMessage="Show less" />}
                showAllLabel={<FormattedMessage id="app.actionButtons.show.all" defaultMessage="Show all" />}
                indicationContainerClass={commonStyles['warning-indication']}
                {...includeFailedValidationData}
            />
        </FormNamespaceProvider>
    );
};

const initialPBPMyAudiencesFilter = {
    selectedFilter: {
        name: 'Type',
        fieldName: 'type',
    },
    selectedSubFilters: [
        {
            filterValue: MY_AUDIENCES_TYPES.PIXEL_BASED_PREDICTIVE,
            filterName: 'Predictive Audiences',
        },
    ],
};

export const MyAudiences = props => {
    const [selectedFiltersWithSubFiltersMap, setSelectedFiltersWithSubFiltersMap] = useFiltersGlobalState();
    const { setSelectedFiltersWithSubFilters } = useDropdownFiltersMap({
        selectedFiltersWithSubFiltersMap,
        setSelectedFiltersWithSubFiltersMap,
    });

    const { selectedAccountId } = props;

    const {
        includeAudiences,
        excludeAudiences,
        clearExcludedItems,
        clearIncludedItems,
        removeIncludeAudience,
        removeExcludeAudience,
        clearNonPBPIncludedItems,
        nonPbpAudiences,
        pbpAudiences,
        hasIncludedPbpAudiences,
        hasIncludedNonPbpAudiences,
        myAudiences,
        setTargetingTypeValue,
        addItem,
        isDisabled,
        isLoadingMyAudiences,
        targetingType,
        allSegmentsByIdMap,
        isOptionsLoadedSuccessfully,
        rejectedAudiencesList,
        isRejectedAudiencesLoadedSuccessfully,
        isDirty,
    } = useMyAudiences(selectedAccountId);

    const isTargetingTypeInclude = targetingType === TARGETING_TYPES.INCLUDE;

    const { openPreventPBPAudienceMixingModal, shouldPreventPBPAudienceMixing } = usePreventPBPAudiencesMixing({
        clearNonPBPIncludedItems,
        isTargetingTypeInclude,
    });

    const { openBlockPBPAudienceModal, shouldBlockAudience } = useBlockPBPAudience({
        selectedAccountId,
    });

    const [temporarySelection, setTemporarySelection] = useState([]);
    const temporarySelectionHasNonPbpAudiences = useMemo(() => {
        return some(nonPbpAudiences, audience => temporarySelection.includes(audience.id));
    }, [temporarySelection, nonPbpAudiences]);
    const temporarySelectionHasPbpAudiences = useMemo(() => {
        return some(pbpAudiences, audience => temporarySelection.includes(audience.id));
    }, [pbpAudiences, temporarySelection]);
    const selectionContainsPbp = Boolean(hasIncludedPbpAudiences || temporarySelectionHasPbpAudiences);

    const { filters, getAudienceTypeMatchingInput } = useMyAudiencesFilters(myAudiences);
    const shouldHaveDefaultPBPFilter = useMemo(() => {
        return shouldPreventPBPAudienceMixing && selectionContainsPbp;
    }, [shouldPreventPBPAudienceMixing, selectionContainsPbp]);

    const checkPBPIncludedAndSetSelectedFilter = useCallback(() => {
        if (!shouldPreventPBPAudienceMixing || !selectionContainsPbp) {
            return;
        }

        const typeFilter = {
            name: 'Type',
            fieldName: 'type',
        };

        const PPSubFilter = [
            {
                filterValue: MY_AUDIENCES_TYPES.PIXEL_BASED_PREDICTIVE,
                filterName: 'Predictive Audiences',
            },
        ];
        setSelectedFiltersWithSubFilters({ selectedFilter: typeFilter, selectedSubFilters: PPSubFilter });
    }, [selectionContainsPbp, setSelectedFiltersWithSubFilters, shouldPreventPBPAudienceMixing]);

    const myAudiencesMenuListRenderer = useMemo(
        () =>
            createMenuListWithHeader({
                filters,
                setSelectedFiltersWithSubFilters,
                initialFilter: shouldHaveDefaultPBPFilter ? initialPBPMyAudiencesFilter : {},
            }),
        [filters, setSelectedFiltersWithSubFilters, shouldHaveDefaultPBPFilter]
    );

    const addItemToTemporarySelection = useCallback(
        value => {
            temporarySelection.includes(value.id)
                ? setTemporarySelection(temporarySelection.filter(id => id !== value.id))
                : setTemporarySelection([...temporarySelection, value.id]);
        },
        [temporarySelection]
    );
    const [searchInputValue, setSearchInputValue] = useState('');

    const onMenuClose = useCallback(() => {
        setSearchInputValue('');
        temporarySelection.forEach(id => {
            addItem({ id });
        });
        setTemporarySelection([]);
        setSelectedFiltersWithSubFiltersMap({});
    }, [addItem, setSelectedFiltersWithSubFiltersMap, temporarySelection]);

    const shouldRenderTargetingLists = useMemo(() => {
        return isOptionsLoadedSuccessfully && (!isEmpty(includeAudiences) || !isEmpty(excludeAudiences));
    }, [includeAudiences, excludeAudiences, isOptionsLoadedSuccessfully]);

    const myAudienceDropDownOptionCheckbox = useMemo(
        () =>
            ({ data, selectProps }) => {
                const selectionContainsPbp = hasIncludedPbpAudiences || temporarySelectionHasPbpAudiences;
                const isNonPbpAudience = data.type !== MY_AUDIENCES_TYPES.PIXEL_BASED_PREDICTIVE;

                const isDisabledFromData = data.isDisabled;
                const isDisabledFromPbpAudiences =
                    shouldPreventPBPAudienceMixing && selectionContainsPbp && isNonPbpAudience;

                return (
                    <MyAudienceDropDownOptionCheckbox
                        data={{
                            ...data,
                            isDisabled: isDisabledFromPbpAudiences || isDisabledFromData,
                        }}
                        selectProps={selectProps}
                        selectedSegments={temporarySelection || []}
                        disabledTooltip={isDisabledFromPbpAudiences ? nonPbpDisabledTooltip : disabledTooltip}
                    />
                );
            },
        [hasIncludedPbpAudiences, shouldPreventPBPAudienceMixing, temporarySelection, temporarySelectionHasPbpAudiences]
    );

    const filterOptions = useCallback(
        (option, inputValue) => {
            return (
                !option ||
                !option.label ||
                !option.data ||
                option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
                option.data.id?.toString().startsWith(inputValue) ||
                option.data.externalId?.toString().startsWith(inputValue) ||
                option.data.type === getAudienceTypeMatchingInput(inputValue)
            );
        },
        [getAudienceTypeMatchingInput]
    );

    const isMyAudiencesFiltersAllowed = useConfigMatch({
        [FEATURE_FLAGS.MY_AUDIENCES_FILTERS_ENABLED]: 'true',
    });

    const handleInputChange = (query, { action }) => {
        if (action !== 'set-value') {
            setSearchInputValue(query);
        }
        return searchInputValue;
    };

    const mainDropDownComponentProps = isMyAudiencesFiltersAllowed
        ? {
              menuListRenderer: myAudiencesMenuListRenderer,
              closeMenuOnSelect: false,
              blurInputOnSelect: false,
              onMenuClose,
              filterOption: filterOptions,
              inputValue: searchInputValue,
              onInputChange: handleInputChange,
          }
        : {};

    const myAudiencesDropdownOption = isMyAudiencesFiltersAllowed
        ? myAudienceDropDownOptionCheckbox
        : MyAudienceDropDownOption;

    const myAudiencesOnAdd = useCallback(
        async item => {
            if (shouldBlockAudience(item)) {
                await openBlockPBPAudienceModal(item);
                return;
            }

            const isPbpAudience = item.type === MY_AUDIENCES_TYPES.PIXEL_BASED_PREDICTIVE;
            const selectionContainsNonPbp = hasIncludedNonPbpAudiences || temporarySelectionHasNonPbpAudiences;
            const shouldShowPreventPBPAudienceMixingModal =
                shouldPreventPBPAudienceMixing && isPbpAudience && selectionContainsNonPbp;

            if (shouldShowPreventPBPAudienceMixingModal) {
                const shouldAddItem = await openPreventPBPAudienceMixingModal(item);
                if (shouldAddItem) {
                    setTemporarySelection([]);
                    addItem(item);
                    checkPBPIncludedAndSetSelectedFilter(); // The check for PBP is duplicated, but the function is also used where the check is not done outside the function
                }
                return;
            }

            // Disabling the dropdown item does not disable selection of the item
            // This prevents the item from being added to the selection
            const shouldPreventNonPBPAudienceSelection =
                shouldPreventPBPAudienceMixing && !isPbpAudience && selectionContainsPbp;

            if (shouldPreventNonPBPAudienceSelection) {
                return;
            }

            const addItemFunc = isMyAudiencesFiltersAllowed ? addItemToTemporarySelection : addItem;
            addItemFunc(item);

            if (isPbpAudience) {
                checkPBPIncludedAndSetSelectedFilter();
            }
        },
        [
            shouldBlockAudience,
            hasIncludedNonPbpAudiences,
            temporarySelectionHasNonPbpAudiences,
            shouldPreventPBPAudienceMixing,
            selectionContainsPbp,
            isMyAudiencesFiltersAllowed,
            addItemToTemporarySelection,
            addItem,
            openBlockPBPAudienceModal,
            openPreventPBPAudienceMixingModal,
            checkPBPIncludedAndSetSelectedFilter,
        ]
    );

    const myAudiencesDropdownPlaceholder = isMyAudiencesFiltersAllowed ? (
        <FormattedMessage
            id="campaign.editor.targeting.my.audiences.select.placeholder.with.filters"
            defaultMessage="Enter a search term or select filters"
        />
    ) : (
        <FormattedMessage
            id="campaign.editor.targeting.my.audiences.select.placeholder"
            defaultMessage="Start typing to select an audience..."
        />
    );

    return (
        <div data-testid="myAudienceListContainer">
            {isDisabled && Tooltip}
            <TargetingDropdownBox
                itemType={targetingType}
                onSelectItemType={setTargetingTypeValue}
                itemPlaceholder={myAudiencesDropdownPlaceholder}
                onAddItem={myAudiencesOnAdd}
                allItems={myAudiences}
                itemOptionRenderer={myAudiencesDropdownOption}
                itemTypeOptionRenderer={props => <TargetingTypeDropdownOption isDisabled={isDisabled} {...props} />}
                itemSelectionDisabled={isDisabled}
                isItemTypeDisabled={isDisabled}
                isItemsLoading={isLoadingMyAudiences}
                mainDropDownComponentProps={mainDropDownComponentProps}
            />
            {shouldRenderTargetingLists ? (
                <>
                    <IncludeCollapsibleList
                        includeAudiences={includeAudiences}
                        targetingType={targetingType}
                        removeMyAudience={removeIncludeAudience}
                        clearIncludedItems={clearIncludedItems}
                        allSegmentsByIdMap={allSegmentsByIdMap}
                        isOptionsLoadedSuccessfully={isOptionsLoadedSuccessfully}
                        rejectedAudiencesList={rejectedAudiencesList}
                        isRejectedAudiencesLoadedSuccessfully={isRejectedAudiencesLoadedSuccessfully}
                        isDirty={isDirty}
                        aria-label="include-list"
                    />
                    <CollapsibleListWithIndication
                        items={excludeAudiences}
                        ItemComponent={MyAudiencesListItem}
                        metadata={{ targetingType }}
                        deleteItem={removeExcludeAudience}
                        clearItems={clearExcludedItems}
                        listHeaderTitle={
                            <FormattedMessage
                                id="campaign.editor.targeting.my.audiences.title.excluded"
                                defaultMessage="Excluded Audiences"
                            />
                        }
                        aria-label="exclude-list"
                        showLessLabel={<FormattedMessage id="app.actionButtons.show.less" defaultMessage="Show less" />}
                        showAllLabel={<FormattedMessage id="app.actionButtons.show.all" defaultMessage="Show all" />}
                        containerClassName={styles['list']}
                    />
                </>
            ) : null}
        </div>
    );
};
